import { type JSX, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import { AppBackground } from "@/components/app-background";

/**
 * Apunte Digital's OWN auth shell — deliberately NOT the shared `AuthLayout`
 * (4 variants + glass `AuthCard`) that made every product read as a clone, and
 * deliberately distinct from the other clones' choices:
 *
 *   - listto took "cardless full-bleed editorial column".
 *   - daily-journal took the intimate "lined-paper journal page".
 *   - Apunte Digital takes the **index-card / Zettelkasten note sheet**: a
 *     physical index card with a punched binding edge, a coloured **margin
 *     rule** down the left (the classic ruled-card gutter) and a tab/masthead
 *     band on top carrying the wordmark + a per-screen "card index" eyebrow.
 *
 *   ┌─ apunte digital ───────────────── · card · ──┐  ← masthead tab band
 *   │  Big editorial headline.                       │
 *   │  Supporting line.                              │
 *   ├──┬─────────────────────────────────────────────┤
 *   │○ │  email   [______________]                   │  ← index-card sheet:
 *   │○ │  pass    [______________]                   │     punched edge (○○○) +
 *   │○ │  [ Sign in ]                                 │     coloured margin rule
 *   └──┴─────────────────────────────────────────────┘
 *
 * The note-card is its own surface (NOT `GlassSurface`/`AuthCard`), so two
 * products opened side by side don't share a silhouette. Screens pour their
 * fields in as `children`; the shell is product-agnostic about the form and
 * owns only the chrome, the brand, the scroll container and the safe-areas.
 *
 * ── SCROLL CONTRACT (kit #13 fix, same root cause as melodimals #4) ─────────
 * `AppBackground` is `position: fixed; overflow: hidden` and clips its children,
 * so it is rendered here as a pure decorative backdrop SIBLING. The content
 * lives in its own scroll container: `min-h-[100dvh] overflow-y-auto` (never
 * `h-dvh`/`overflow-hidden`), with a `min-h-full` inner column, so a tall form
 * (register) or a short landscape viewport is always reachable by scrolling.
 */

interface ApunteSceneProps {
  /** Eyebrow chip rendered on the masthead tab (e.g. "Card index"). Optional. */
  eyebrow?: JSX.Element;
  /** Editorial headline for this screen. Optional for bare loading states. */
  headline?: JSX.Element;
  /** Supporting line under the headline. */
  tagline?: JSX.Element;
  /**
   * Heading level for the headline. The masthead headline IS the page h1 for
   * Apunte's auth (the card has no title). Pass "h2" only if the screen owns
   * its own h1 elsewhere.
   */
  headingLevel?: "h1" | "h2";
  /** Column width. "narrow" for login/forgot/reset/verify, "wide" for register. */
  width?: "narrow" | "wide";
  /** The form (fields + actions), rendered on the index-card sheet. */
  children: JSX.Element;
  /** Footer slot (links: register / back home), rendered under the card. */
  footer?: JSX.Element;
}

export function ApunteScene(props: ApunteSceneProps) {
  const Heading = (headingProps: { children: JSX.Element }) =>
    (props.headingLevel ?? "h1") === "h2" ? (
      <h2 class="text-balance font-serif text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl">
        {headingProps.children}
      </h2>
    ) : (
      <h1 class="text-balance font-serif text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl">
        {headingProps.children}
      </h1>
    );

  return (
    <>
      {/* Fixed decorative backdrop — NEVER the element that scrolls. */}
      <AppBackground />

      {/* Real scroll container: grows past the viewport and scrolls when the
          card doesn't fit (portrait or short landscape). */}
      <div
        class="relative z-10 min-h-[100dvh] w-full overflow-x-hidden overflow-y-auto px-5 sm:px-6"
        style={{
          "padding-top": "max(2rem, env(safe-area-inset-top))",
          "padding-bottom": "max(2rem, env(safe-area-inset-bottom))",
          "padding-left": "max(1.25rem, env(safe-area-inset-left))",
          "padding-right": "max(1.25rem, env(safe-area-inset-right))",
        }}
      >
        <div
          class="mx-auto flex min-h-full w-full flex-col justify-center py-8"
          classList={{
            "max-w-md": (props.width ?? "narrow") === "narrow",
            "max-w-lg": props.width === "wide",
          }}
        >
          {/* Masthead tab band — wordmark + per-screen card-index eyebrow. */}
          <header class="px-1">
            <div class="flex items-center justify-between gap-3 pb-4">
              <Link
                to="/"
                class="group flex items-center gap-2.5 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                // oxlint-disable-next-line lingui/no-unlocalized-strings -- brand name (proper noun), not translatable UI text
                aria-label="Apunte Digital"
              >
                <span class="grid h-9 w-9 place-items-center rounded-lg bg-accent text-accent-foreground shadow-sm ring-1 ring-black/5 transition-transform group-hover:scale-105 group-active:scale-95">
                  {/* '#' mark — a markdown heading / index tag for Apunte. */}
                  <span class="font-serif text-lg font-bold leading-none" aria-hidden="true">
                    #
                  </span>
                </span>
                {/* oxlint-disable-next-line lingui/no-unlocalized-strings -- brand wordmark (proper noun), not translatable UI text */}
                <span class="font-serif text-base font-semibold tracking-tight text-foreground">
                  Apunte Digital
                </span>
              </Link>
              <Show when={props.eyebrow}>
                <span class="inline-flex items-center rounded-md bg-accent/15 px-2.5 py-1 font-mono text-[0.7rem] font-semibold uppercase tracking-widest text-accent ring-1 ring-accent/25">
                  {props.eyebrow}
                </span>
              </Show>
            </div>

            <Show when={props.headline}>
              <div class="space-y-2 pb-5">
                <Heading>{props.headline}</Heading>
                <Show when={props.tagline}>
                  <p class="text-pretty text-[0.95rem] leading-relaxed text-muted-foreground">
                    {props.tagline}
                  </p>
                </Show>
              </div>
            </Show>
          </header>

          {/* Index-card sheet: punched binding edge + coloured margin rule.
              This is its OWN surface (not GlassSurface) so the silhouette is
              unmistakably an index card, not the shared glass frame. */}
          <div class="relative flex min-h-0 overflow-hidden rounded-2xl border border-border bg-surface/95 shadow-lg ring-1 ring-black/5 backdrop-blur-sm">
            {/* Binding gutter: filled left strip with punched holes. */}
            <div
              class="relative w-7 shrink-0 border-r border-border bg-muted/40 sm:w-9"
              aria-hidden="true"
            >
              <div class="absolute inset-y-0 left-1/2 flex -translate-x-1/2 flex-col items-center justify-center gap-5 py-6">
                <span class="h-2 w-2 rounded-full bg-border ring-1 ring-black/5" />
                <span class="h-2 w-2 rounded-full bg-border ring-1 ring-black/5" />
                <span class="h-2 w-2 rounded-full bg-border ring-1 ring-black/5" />
              </div>
            </div>

            {/* Card body with the classic coloured margin rule near the gutter. */}
            <div class="relative min-w-0 flex-1">
              <div
                class="absolute inset-y-0 left-3 w-px bg-accent/40 sm:left-4"
                aria-hidden="true"
              />
              <div class="min-w-0 py-7 pl-6 pr-5 sm:pl-8 sm:pr-7">{props.children}</div>
            </div>
          </div>

          <Show when={props.footer}>
            <footer class="mt-6 space-y-3 px-1 text-center">{props.footer}</footer>
          </Show>
        </div>
      </div>
    </>
  );
}
